"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNav() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-4">
        <div className="h-6 w-16 bg-muted animate-pulse rounded-none" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-none" />
        <div className="h-6 w-16 bg-muted animate-pulse rounded-none" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-default text-sm font-bold uppercase tracking-tight text-foreground hover:text-ring transition-colors px-2 py-1 relative group">
                <span className="font-mono text-[10px] mr-1 opacity-50">0{i + 1}</span>
                {item.label}
                <span className="absolute bottom-0 left-1/2 w-0 h-[2px] bg-ring transition-all duration-300 group-hover:w-full group-hover:left-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl flex flex-col">
      <div className="container mx-auto px-4 py-6 flex flex-col gap-2">
        {loading ? <div className="flex flex-col gap-4">
            <div className="h-10 w-full bg-muted animate-pulse rounded-none" />
            <div className="h-10 w-full bg-muted animate-pulse rounded-none" />
          </div> : menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} onClick={onClose} className="font-default text-lg font-bold uppercase tracking-tight block py-4 text-foreground hover:text-ring border-b border-border/30 transition-colors">
                <span className="font-mono text-xs mr-3 opacity-40">0{i + 1}</span>
                {item.label}
              </Link>
            </div>)}
        <div className="pt-6 pb-2">
          <Button asChild variant="outline" className="w-full rounded-none border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default font-bold uppercase tracking-tight h-12" onClick={onClose}>
            <Link to="/ConsultationPage">Get Consultation</Link>
          </Button>
        </div>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="233276" id="header" className={`sticky top-0 z-50 w-full transition-all duration-400 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border shadow-md" : ""}`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        <Link to="/HomePage" className="flex-shrink-0 z-10">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1658340/img/alpine_engineering_logo.png" className="h-8 w-auto" alt="Alpine Engineering" />
        </Link>

        <div className="hidden md:flex items-center gap-8 lg:gap-12">
          <MenuProvider menu_id="16">
            <DesktopNav />
          </MenuProvider>

          <Button asChild variant="outline" className="rounded-none border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground font-default font-bold uppercase tracking-tight px-8 h-11 transition-all duration-300">
            <Link to="/ConsultationPage">Get Consultation</Link>
          </Button>
        </div>

        <Button variant="ghost" size="icon" className="md:hidden z-10 outline-none text-foreground hover:bg-accent" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle Menu" aria-expanded={isMobileMenuOpen}>
          {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </Button>
      </div>

      <div className="md:hidden">
        <MenuProvider menu_id="16">
          <MobileNav isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
        </MenuProvider>
      </div>
    </section>;
}